import React from 'react';
import {Platform, View, Animated, TextStyle} from 'react-native';
import {LogoSVG, Logo} from '../../../assets/images';
import {isActiveMenu} from '../../pipes';
import {Screens} from '../../routing';
import {desktopCollapsedMenuHeight, desktopMenuHeight, useFontSizeFactor} from '../../theme/helpers';
import {Image} from '../image';
import {Link} from '../link';
import {HelpLinks} from './components/helpLinks';
import {InputModel} from './interface';
import {Text} from '../text';
import {CityWidget} from './components/city';
import {NotificationsAndUser} from './components/NotificationsAndUser';
import {themeSelector} from '../../../redux/selectors/runtime/theme';
import {useSelector} from 'react-redux';
import {GetStyles} from './style';

const headerContentTopHeight = 67;
const transition = Platform.OS === 'web' ? {transition: '0.4s'} : undefined;
const minScrollYForChangeCollapsed = 10;

export default (props: InputModel) => {
  const {navigation, route} = props;
  const fontSizeFactor = useFontSizeFactor();
  const {visuallyImpaired} = useSelector(themeSelector());

  const styles = GetStyles(visuallyImpaired);
  const [shouldCollapse, setShouldCollapse] = React.useState<boolean>(false);
  const logoHeight = shouldCollapse ? 98 : 132;
  const logoWidth = shouldCollapse ? 124 : 168;
  const calculatedMenuHeight = shouldCollapse ? desktopCollapsedMenuHeight : desktopMenuHeight;

  React.useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    const listener = () => setShouldCollapse(window.scrollY > minScrollYForChangeCollapsed);
    window.addEventListener('scroll', listener);
    return () => window.removeEventListener('scroll', listener);
  }, []);

  const getLinkTextStyle = React.useCallback(
    (screen: Screens) => {
      const textStyle: TextStyle[] = [{fontSize: 17 * fontSizeFactor}];

      if (isActiveMenu(screen, route.name)) {
        textStyle.push(styles.activeLink);
      }
      return textStyle;
    },
    [route.name, fontSizeFactor],
  );

  return (
    <Animated.View style={[styles.header, {height: calculatedMenuHeight}, shouldCollapse ? styles.headerShadow : null]}>
      <View style={[styles.border, {top: shouldCollapse ? -1 : headerContentTopHeight}]} />
      <View style={styles.subContainer}>
        <Animated.View style={styles.headerLogo}>
          <View style={[styles.headerLink, shouldCollapse ? styles.headerLinkCollapsed : styles.headerLinkFull]}>
            <Link
              testID='main-logo-link-id'
              accessibilityLabel='Главная'
              dataSetClass='logo'
              screen={Screens.Main}
              navigate={navigation.navigate}>
              <Image
                source={(Platform.OS === 'web' ? LogoSVG : Logo) as any}
                height={logoHeight}
                width={logoWidth}
                style={{width: logoWidth, height: logoHeight, ...transition}}
              />
            </Link>
          </View>
        </Animated.View>
        <View style={[styles.headerContent, {height: calculatedMenuHeight}]}>
          <Animated.View
            style={[
              styles.headerContentTop,
              shouldCollapse ? styles.headerContentTopCollapsed : styles.headerContentTopFullSize,
            ]}>
            <HelpLinks />
            <CityWidget />
            {shouldCollapse || (
              <View style={styles.pixelPerfectUserInfo}>
                <NotificationsAndUser />
              </View>
            )}
          </Animated.View>
          <View style={[styles.menu, shouldCollapse ? styles.menuCollapsed : styles.menuFullSize]}>
            <Link
              testID='card-service-link-id'
              screen={Screens.CardServices}
              navigate={navigation.navigate}
              dataSetClass='header-menu-link'
              style={styles.link}>
              <Text testID='card-service-text-id' style={getLinkTextStyle(Screens.CardServicesMenu)}>
                {shouldCollapse ? 'Возможности' : 'Возможности карты'}
              </Text>
            </Link>
            <Link
              testID='support-service-link-id'
              screen={Screens.SupportService}
              navigate={navigation.navigate}
              dataSetClass='header-menu-link'
              style={styles.link}>
              <Text testID='support-service-text-id' style={getLinkTextStyle(Screens.SupportServiceTab)}>
                {shouldCollapse ? 'Поддержка' : 'Служба поддержки'}
              </Text>
            </Link>
            <Link
              testID='polls-link-id'
              screen={Screens.Polls}
              navigate={navigation.navigate}
              dataSetClass='header-menu-link'
              style={styles.link}>
              <Text testID='polls-text-id' style={getLinkTextStyle(Screens.PollsMenu)}>
                Опросы и голосования
              </Text>
            </Link>
            <Link
              testID='news-link-id'
              screen={Screens.News}
              navigate={navigation.navigate}
              dataSetClass='header-menu-link'
              style={[styles.link, styles.lastLink]}>
              <Text testID='news-text-id' style={getLinkTextStyle(Screens.News)}>
                Новости
              </Text>
            </Link>
            {shouldCollapse && !visuallyImpaired && (
              <View style={styles.iconContainer}>
                <HelpLinks collapsed />
                <CityWidget collapsed />
              </View>
            )}
            {shouldCollapse && <NotificationsAndUser />}
          </View>
        </View>
      </View>
    </Animated.View>
  );
};
